import { Link } from "react-router-dom";
import { Clock, CheckCircle2, XCircle, Mail, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function PendingApproval() {
  const { user, logout } = useAuth();
  const status = user?.approval_status;

  if (status === "rejected") {
    return (
      <div data-testid="rejected-page" className="container-luxe section-pad max-w-2xl text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-destructive/10 text-destructive flex items-center justify-center">
          <XCircle className="w-8 h-8" />
        </div>
        <h1 className="font-display text-4xl text-aubergine mt-6">Application not accepted</h1>
        <p className="mt-4 text-plumSoft font-light">
          {user.rejection_reason || "We weren't able to accept your application at this time."}
        </p>
        <p className="mt-2 text-sm text-plumSoft">
          You're welcome to reapply in 6 months, or write to us if you'd like more context.
        </p>
        <div className="mt-8 flex justify-center gap-3">
          <Link to="/contact" className="btn-primary">Contact us</Link>
          <button onClick={logout} className="btn-ghost">Sign out</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="pending-page" className="container-luxe section-pad max-w-2xl text-center">
      <div className="relative inline-flex">
        <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-champagne/30 to-rose/20 flex items-center justify-center">
          <Clock className="w-9 h-9 text-rose" />
        </div>
        <Sparkles className="absolute -top-2 -right-2 w-5 h-5 text-champagne animate-float" />
      </div>
      <p className="overline mt-7 justify-center flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-teal" /> Application Received</p>
      <h1 className="font-display text-4xl md:text-5xl text-aubergine mt-4 leading-tight">
        You're on the list, <em className="text-rose not-italic">{user?.name?.split(" ")[0] || "there"}.</em>
      </h1>
      <p className="mt-5 text-plumSoft font-light text-lg leading-relaxed">
        Thank you for applying to the WOWSHE founding cohort. The chapter leadership
        will review your application within <strong className="text-aubergine font-normal">5 working days</strong>.
      </p>
      <p className="mt-3 text-plumSoft font-light">
        Once approved, you'll receive an email at <strong className="text-aubergine font-normal">{user?.email}</strong> and can
        immediately access your dashboard.
      </p>

      <div className="mt-10 card-luxe p-8 text-left">
        <p className="overline mb-4">What happens next</p>
        <ol className="space-y-3 text-sm text-plumSoft font-light">
          <Step n={1} title="Review by chapter leadership" desc="Our Hyderabad president, VP and secretary review every applicant." done />
          <Step n={2} title="Email confirmation" desc="We'll email you with the decision within 5 working days." />
          <Step n={3} title="Sign in to your dashboard" desc="Set up your profile, RSVP for events and start building." />
          <Step n={4} title="Activate Annual Membership" desc="Optional — unlock referrals, workshops, wellness programs." />
        </ol>
      </div>

      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <Link to="/" className="btn-primary">Explore WOWSHE</Link>
        <Link to="/contact" className="btn-ghost"><Mail className="w-4 h-4" /> Talk to chapter lead</Link>
        <button data-testid="pending-logout" onClick={logout} className="text-sm text-plum link-underline ml-2 self-center">Sign out</button>
      </div>
    </div>
  );
}

function Step({ n, title, desc, done }) {
  return (
    <li className="flex items-start gap-4">
      <span className={`flex-none w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${done ? "bg-teal text-ivory" : "bg-lavender text-plum"}`}>
        {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : n}
      </span>
      <div>
        <p className="font-display text-aubergine">{title}</p>
        <p className="mt-0.5">{desc}</p>
      </div>
    </li>
  );
}
